import React, { useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Cria ícone customizado com nome da usina visível no mapa
function createSiteIcon(site, isHighlighted) {
    const status = site.realtime?.plantStatus;
    const color = status === 1 ? '#52c41a' : status === 0 ? '#1890ff' : '#ff4d4f';
    const border = isHighlighted ? '3px solid #1890ff' : `3px solid ${color}`;
    const shadow = isHighlighted ? '0 0 0 4px rgba(24,144,255,0.4)' : '0 2px 8px rgba(0,0,0,0.4)';
    const pvPower = site.realtime?.pvPower ? (site.realtime.pvPower / 1000).toFixed(1) : null;
    const powerText = pvPower ? `<span style="font-size:10px;color:${color};font-weight:600;">${pvPower} kW</span>` : '';

    const html = `
        <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            cursor: pointer;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        ">
            <div style="
                background: #fff;
                border: ${border};
                border-radius: 8px;
                padding: 4px 8px;
                font-size: 11px;
                font-weight: 700;
                color: #222;
                white-space: nowrap;
                max-width: 140px;
                overflow: hidden;
                text-overflow: ellipsis;
                box-shadow: ${shadow};
                text-align: center;
                line-height: 1.3;
            ">
                <span style="font-size:13px;">☀️</span>
                ${site.name}
                ${powerText ? `<br/>${powerText}` : ''}
            </div>
            <div style="
                width: 0; height: 0;
                border-left: 6px solid transparent;
                border-right: 6px solid transparent;
                border-top: 8px solid ${color};
                margin-top: -1px;
            "></div>
        </div>
    `;

    return L.divIcon({
        html,
        className: '',
        iconAnchor: [70, 52],
        popupAnchor: [0, -50],
    });
}

function MapBounds({ sites }) {
    const map = useMap();
    const positions = useMemo(() => {
        return sites
            .map((s) => {
                const lat = parseFloat(s.latitude);
                const lng = parseFloat(s.longitude);
                return isNaN(lat) || isNaN(lng) ? null : [lat, lng];
            })
            .filter(Boolean);
    }, [sites]);

    useEffect(() => {
        if (positions.length === 0) return;
        if (positions.length === 1) {
            map.setView(positions[0], 14);
        } else {
            map.fitBounds(positions, { padding: [60, 60], maxZoom: 14 });
        }
    }, [map, positions]);

    return null;
}

const OperacaoSitesMap = ({ sites = [], onSiteClick, highlightedSiteId, height = 400 }) => {
    const sitesWithCoords = useMemo(() => {
        return sites.filter((s) => {
            const lat = parseFloat(s.latitude);
            const lng = parseFloat(s.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        });
    }, [sites]);

    const defaultCenter = useMemo(() => {
        if (sitesWithCoords.length === 0) return [-15.77972, -47.92972];
        const lats = sitesWithCoords.map((s) => parseFloat(s.latitude));
        const lngs = sitesWithCoords.map((s) => parseFloat(s.longitude));
        return [
            (Math.min(...lats) + Math.max(...lats)) / 2,
            (Math.min(...lngs) + Math.max(...lngs)) / 2,
        ];
    }, [sitesWithCoords]);

    if (sitesWithCoords.length === 0) {
        return (
            <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', borderRadius: 8, flexDirection: 'column', gap: 8 }}>
                <span style={{ fontSize: 32 }}>🗺️</span>
                <span style={{ color: '#8c8c8c', fontSize: 14 }}>Aguardando localização das usinas…</span>
                <span style={{ color: '#bfbfbf', fontSize: 12 }}>As coordenadas são buscadas automaticamente pelo endereço.</span>
            </div>
        );
    }

    return (
        <div style={{ height, width: '100%', borderRadius: 8, overflow: 'hidden' }}>
            <MapContainer
                center={defaultCenter}
                zoom={sitesWithCoords.length === 1 ? 14 : 6}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom
            >
                <MapBounds sites={sitesWithCoords} />
                <TileLayer
                    attribution='Tiles &copy; Esri'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                />
                {sitesWithCoords.map((site) => {
                    const pos = [parseFloat(site.latitude), parseFloat(site.longitude)];
                    const status = site.realtime?.plantStatus;
                    const statusLabel = status === 1 ? '🟢 Gerando' : status === 0 ? '🔵 Aguardando' : '⚫ Offline';
                    const pvPower = site.realtime?.pvPower ? (site.realtime.pvPower / 1000).toFixed(1) : '-';
                    const daily = site.realtime?.dailyGeneration ? site.realtime.dailyGeneration.toFixed(1) : '-';
                    const isHighlighted = highlightedSiteId === site.id;

                    return (
                        <Marker
                            key={`${site.id}-${isHighlighted}`}
                            position={pos}
                            icon={createSiteIcon(site, isHighlighted)}
                            eventHandlers={{ click: () => onSiteClick?.(site) }}
                        >
                            <Popup>
                                <div style={{ minWidth: 160 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>☀️ {site.name}</div>
                                    <div style={{ color: '#666', fontSize: 12, marginBottom: 6 }}>📍 {site.city} - {site.state}</div>
                                    <div style={{ marginBottom: 4 }}>{statusLabel}</div>
                                    <div style={{ display: 'flex', gap: 16 }}>
                                        <div><span style={{ color: '#888', fontSize: 11 }}>Agora</span><br /><strong>{pvPower} kW</strong></div>
                                        <div><span style={{ color: '#888', fontSize: 11 }}>Hoje</span><br /><strong style={{ color: '#52c41a' }}>{daily} kWh</strong></div>
                                    </div>
                                    <div style={{ marginTop: 8, fontSize: 11, color: '#1890ff', cursor: 'pointer', textAlign: 'center' }}>
                                        Clique para abrir o dashboard →
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </div>
    );
};

export default OperacaoSitesMap;
